import { FileCopyOutlined } from '@mui/icons-material';
import { LinkNext } from 'components-v2/atoms/LinkNext/LinkNext';
import { PROMOCODES_TS_WITH_TEXT_ICON } from 'constants/Images';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { BeVoucher } from 'types/voucher';
import { ImageFallbackStoreImage } from 'utils/ImageFallback';
import NotifyUtils from 'utils/NotifyUtils';
import { CountdownExpiredTime } from 'components-v2/ui/CountdownExpiredTime';
import { ButtonVoucherCTA } from 'components-v2/ui/ButtonVoucherCTA';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

export interface VoucherInfoProps {
  title: string;
  code: string;
  expiredDate: string;
  restExpiredTime?: number;
  usedPercent: number;
  content: string;
  condition: string;
  usageTotal: number;
  timePercent: number;
  shouldDisplayTimePercent?: boolean;
  shouldDisplayUsedPercent?: boolean;
  shouldDisplayCountdownExpiredTime?: boolean;
  imageUrl?: string;
  sellerName?: string;
  sellerLink?: string;
  message?: string;
  status?: BeVoucher['actionStatus'];
  isStatusLoading?: boolean;
  onClick?: () => void;
  subTitle?: string;
  conditionList?: React.ReactNode;
  progressBar?: React.ReactNode;
}

export const VoucherInfo = ({
  imageUrl,
  title,
  code,
  expiredDate,
  restExpiredTime,
  shouldDisplayUsedPercent,
  shouldDisplayTimePercent,
  shouldDisplayCountdownExpiredTime,
  usedPercent,
  timePercent,
  content,
  condition,
  sellerLink,
  sellerName,
  message,
  status,
  onClick,
  isStatusLoading,
  usageTotal,
  conditionList,
  progressBar,
}: VoucherInfoProps) => {
  const shouldDisplayMessage = !isStatusLoading && message && message.length > 0;
  const shouldDisplayCTA = !isStatusLoading && !message;

  return (
    <>
      <div className="min-w-[360px] max-w-[40%] flex flex-col space-y-2">
        <h3 className="text-[32px] font-bold line-clamp-2 text-black text-center">{title}</h3>
        <div className="h-[100px] min-w-[100px] relative !my-6 mx-auto">
          <ImageFallbackStoreImage src={imageUrl || PROMOCODES_TS_WITH_TEXT_ICON} layout="fill" objectFit="contain" objectPosition="left" />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-[16px] font-medium min-w-fit">Mã sử dụng: </p>
          <CopyToClipboard text={code} onCopy={() => NotifyUtils.success('Đã copy giá trị')}>
            <div className="flex items-center gap-2 max-w-[90%]">
              <span className="text-[#1D39C4] text-[16px] font-medium cursor-pointer  line-clamp-1">{code}</span>
              <FileCopyOutlined className="text-[#0E1983] !text-[18px] cursor-pointer" />
            </div>
          </CopyToClipboard>
        </div>
        {sellerName && (
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-[16px] font-medium min-w-fit">Trang NBH: </p>
            <LinkNext href={sellerLink} target="_blank">
              <span className="text-[14px] font-[ggsm] border-b-[1px] border-[#1D39C4] text-[#1D39C4] cursor-pointer">{sellerName}</span>
            </LinkNext>
          </div>
        )}
        <p className="text-[#F53449] text-[16px] font-medium">
          {!shouldDisplayCountdownExpiredTime && `Hạn sử dụng: ${expiredDate}`}
          {shouldDisplayCountdownExpiredTime && <CountdownExpiredTime restExpiredTime={restExpiredTime || 0} />}
        </p>
        {shouldDisplayTimePercent && (
          <div className="h-5 w-full rounded-[20px] bg-[#F0F0F0] flex items-center justify-center relative overflow-hidden">
            <div
              className="bg-[#FFB951] absolute inset-0 z-1"
              style={{
                width: timePercent + '%',
              }}
            />
            {!!usageTotal && <p className="text-[10px] relative z-20">Đã dùng: {usageTotal} lượt</p>}
          </div>
        )}
        {shouldDisplayUsedPercent && (
          <div className="h-5 w-full rounded-[20px] bg-[#F0F0F0] flex items-center justify-center relative overflow-hidden">
            <div
              className="bg-[#FFB951] absolute inset-0 z-1"
              style={{
                width: usedPercent + '%',
              }}
            />
            <p className="text-[10px] relative z-20">Đã dùng: {usedPercent}%</p>
          </div>
        )}
        {shouldDisplayCTA && <ButtonVoucherCTA onClick={onClick} status={status} />}
      </div>
      <div className="flex flex-col flex-1 gap-4">
        <div className="flex flex-col">
          <div className="flex justify-between items-center">
            <p className="text-[18px]">
              <strong>Nội dung</strong>
            </p>
            {progressBar}
          </div>
          <p className="font-[ggsr] text-[16px]" dangerouslySetInnerHTML={{ __html: content }} />
        </div>
        <details open className="group">
          <summary className="list-none [&::-webkit-details-marker]:hidden w-full flex items-center gap-2 justify-between cursor-pointer">
            <p className="text-[16px] font-semibold ">Điều kiện sử dụng</p>
            <KeyboardArrowDownIcon className="transition-all group-open:rotate-180 text-[var(--primary-color)]" />
          </summary>
          <div className="flex flex-col gap-2 mt-2">
            <div className="flex gap-2">
              {conditionList && <div className="h-auto w-[1px] bg-[#3FB877] ml-2" />}
              <p
                className="font-[ggsr] text-[16px] max-h-[200px] overflow-auto [&_a]:[text-decoration:revert] [&_a]:[color:revert]"
                dangerouslySetInnerHTML={{
                  __html: condition,
                }}
              />
            </div>
            {conditionList}
          </div>
        </details>
        {shouldDisplayMessage && <i className="text-red-500 mt-1">* {message}</i>}
        {isStatusLoading && <div className="animate-pulse h-4 w-1/2 bg-slate-200 rounded" />}
      </div>
    </>
  );
};

